import { type HotelListItem } from '@utazzo/shared';
import { motion } from 'framer-motion';
import { Image as ImageIcon, MapPin, Star } from 'lucide-react';
import React from 'react';

import { LegacyBadge } from '@/components/legacy-badge';
import { fadeUp } from '@/lib/motion';

/**
 * Hotel card (Vercel/Linear-style refinement).
 *
 * Identity comes from typography + real data, not decoration. The hero is
 * neutral (zinc-50) with a single functional overlay (stars). No rainbow
 * swatches: differentiation = hotel name, location, vendor.
 *
 * Status = 6px dot + neutral text (Linear pattern), not a coloured pill.
 */

interface HotelCardProps {
  hotel: HotelListItem;
  onClick?: (hotel: HotelListItem) => void;
}

const STATUS_TONE: Record<HotelListItem['status'], { dot: string; label: string }> = {
  ACTIVE: { dot: 'bg-emerald-500', label: 'Active' },
  DRAFT: { dot: 'bg-amber-500', label: 'Draft' },
  PENDING_APPROVAL: { dot: 'bg-amber-500', label: 'Pending' },
  INACTIVE: { dot: 'bg-zinc-400', label: 'Inactive' },
  ARCHIVED: { dot: 'bg-zinc-400', label: 'Archived' },
};

/** Stars rendered as 5 fills with empty greys, so they always anchor to the same width. */
const Stars = ({ rating }: { rating: number }): React.JSX.Element => (
  <span className="inline-flex items-center gap-px" aria-label={`${rating} star`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={
          'h-3 w-3 ' +
          (i <= rating ? 'fill-amber-400 text-amber-400' : 'fill-zinc-200 text-zinc-200')
        }
        strokeWidth={0}
      />
    ))}
  </span>
);

export const HotelCard = ({ hotel, onClick }: HotelCardProps): React.JSX.Element => {
  const status = STATUS_TONE[hotel.status];

  return (
    <motion.button
      type="button"
      onClick={() => onClick?.(hotel)}
      variants={fadeUp}
      className={
        'group relative flex flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white text-left ' +
        'transition-[border-color,background-color] duration-150 ' +
        'hover:border-zinc-300 hover:bg-zinc-50/30 ' +
        'focus-visible:border-zinc-400 focus-visible:ring-2 focus-visible:ring-zinc-300 focus-visible:ring-offset-2 focus-visible:outline-none'
      }
    >
      {/* Hero - neutral, photo-placeholder-honest. No gradient. */}
      <div className="relative flex h-28 items-center justify-center border-b border-zinc-100 bg-zinc-50">
        <ImageIcon className="h-6 w-6 text-zinc-300" strokeWidth={1.5} aria-hidden />
        {hotel.starRating > 0 && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded bg-white px-1.5 py-0.5 shadow-sm ring-1 ring-zinc-200/80">
            <Stars rating={hotel.starRating} />
          </span>
        )}
        {hotel.tboHotelCode && (
          <span className="absolute top-2 right-2 rounded bg-white px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-zinc-600 ring-1 ring-zinc-200/80">
            TBO
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2 p-4">
        {/* Name + legacy. Title is the visual anchor. */}
        <div className="flex items-start gap-2">
          <h3 className="line-clamp-1 flex-1 text-sm leading-snug font-semibold tracking-tight text-zinc-900">
            {hotel.name}
          </h3>
          <LegacyBadge legacyId={hotel.legacyId} />
        </div>

        {/* Location + chain (secondary identity, max 2 lines combined) */}
        <div className="flex flex-col gap-0.5">
          <span className="inline-flex items-center gap-1 text-xs text-zinc-600">
            <MapPin className="h-3 w-3 shrink-0 text-zinc-400" strokeWidth={2} aria-hidden />
            <span className="truncate">
              {hotel.cityName ?? <span className="text-zinc-400">No city</span>}
              {hotel.countryName ? `, ${hotel.countryName}` : ''}
            </span>
          </span>
          {hotel.hotelChain && (
            <span className="truncate pl-4 text-xs text-zinc-500">{hotel.hotelChain}</span>
          )}
        </div>

        {/* Meta footer - status dot + rooms count + vendor. Linear-style. */}
        <div className="mt-1 flex items-center gap-3 border-t border-zinc-100 pt-3 text-xs">
          <span className="inline-flex items-center gap-1.5 text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + status.dot} aria-hidden />
            {status.label}
          </span>
          <span className="text-zinc-300" aria-hidden>
            ·
          </span>
          <span className="text-zinc-600 tabular-nums">
            {hotel.roomCount} room{hotel.roomCount === 1 ? '' : 's'}
          </span>
          <span className="flex-1" />
          {hotel.vendorSupplierName && (
            <span className="max-w-[120px] truncate text-zinc-500" title={hotel.vendorSupplierName}>
              {hotel.vendorSupplierName}
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
};
